import { template } from "@/lib/pdfme/templates/eligibility-information";
import { Font } from "@pdfme/common";
import { generate } from "@pdfme/generator";
import { barcodes, image, text } from "@pdfme/schemas";

/**
 * 資格情報のお知らせPDF専用のInput型
 */
type EligibilityInformationInput = {
  // top section
  topDownloadedAt: string;
  topOfficeCode: string;
  topInsuranceCode: string;
  topNameOfPrimary: string;
  topName: string;

  // table section
  tableOfficeCode: string;
  tableInsuranceCode: string;
  tableName: string;
  tableNameKana: string;
  tableDateOfBirth: string;
  tableAcquiredAt: string;
  tableHealthcareSocietyName: string;
  tableCoPaymentRatio: string;

  // card section
  cardOfficeCode: string;
  cardInsuranceCode: string;
  cardBranchCode: string;
  cardNameKana: string;
  cardName: string;
  cardDateOfBirth: string;
  cardAcquiredAt: string;
  cardHealthcareSocietyNumber: string;
  cardHealthcareSocietyName: string;
};

/**
 * 名前の長さに基づいてテンプレートを最適化
 */
const createEligibilityInformationTemplate = ({
  nameKana,
  name,
}: {
  nameKana: string;
  name: string;
}) => {
  const adjustedTemplate = JSON.parse(JSON.stringify(template));

  const nameKanaLength = (nameKana || "").length;
  const nameLength = (name || "").length;
  const isShortName = nameKanaLength <= 15 && nameLength <= 15;

  // cardNameKanaとcardNameフィールドを探して調整
  const schemas = adjustedTemplate.schemas[0];

  for (const schema of schemas) {
    if (schema.name === "cardNameKana" || schema.name === "cardName") {
      if (isShortName) {
        // 短い名前の場合：他のcardフィールドと統一（右寄せ）+ Y位置調整
        schema.position.x = 39.46;
        if (schema.name === "cardNameKana") {
          schema.position.y = 251.4;
        } else if (schema.name === "cardName") {
          schema.position.y = 256.0;
        }
      } else {
        // 長い名前の場合：左寄せを維持し、動的フォントサイズを追加
        schema.position.x = 18.46;
        schema.dynamicFontSize = {
          min: 8,
          max: 10,
          fit: "auto",
        };
      }
    }
  }

  return adjustedTemplate;
};

/**
 * 資格情報のお知らせPDFを生成
 */
export const generateEligibilityInformationPdf = async ({
  input,
  font,
}: {
  input: EligibilityInformationInput;
  font: Font;
}): Promise<Uint8Array> => {
  const adjustedTemplate = createEligibilityInformationTemplate({
    nameKana: input.cardNameKana || "",
    name: input.cardName || "",
  });

  return await generate({
    template: adjustedTemplate,
    plugins: {
      text,
      image,
      qrcode: barcodes.qrcode,
    },
    options: { font },
    inputs: [input],
  });
};

/** @knipignore */
export const forTest = {
  createEligibilityInformationTemplate,
};
